/* eslint-disable react/no-danger */

import React from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import classnames from 'classnames';

import { UrlParams } from 'common/url-params';
import { ListingToolbar } from 'components/common';
import { ProductList } from 'components/product';
import { ImageMessage, Pagination } from 'components/ui';
import { IMAGES } from 'constants/images';
import { APP_ROUTES } from 'constants/routes';
import { useAnalyticsViewItemList } from 'hooks/useAnalyticsViewItemList';
import { useRouteQueryAnalyticsListProps } from 'hooks/useRouteQueryAnalyticsListProps';

import ProductsListingNavigation from './ProductsListingNavigation';

import type { ProductDefaultCard } from 'components/product';
import type { PaginationOnChangeEvent } from 'components/ui';

import type {
  IAnalyticsListProps, IListingBanner, IListingCategories, IListingFilters, IListingOrderBy, IPageUrlParams, IProduct,
} from 'types';

import styles from './ProductsListing.module.scss';

interface Props {
  className?: string,
  emptyClassName?: string,
  //
  urlParams: IPageUrlParams,
  //
  title?: string,
  banners?: IListingBanner[],
  //
  navigation?: any,
  categoryCode?: string,
  categories?: IListingCategories,
  //
  products: IProduct[],
  //
  count?: number,
  totalPages: number,
  activePage: number,
  //
  productListProps?: IAnalyticsListProps,
  productCardProps?: Partial<React.ComponentProps<typeof ProductDefaultCard>>,
  promotion?: {code: string, name: string},
  //
  orderBy?: IListingOrderBy,
  filters?: IListingFilters,
  //
  scrollToProducts?: boolean,
  wide?: boolean,
  dataTestId?: string
}

const ProductsListing = (props: Props) => {

  const {
    className,
    emptyClassName,
    //
    categoryCode,
    urlParams,
    categories,
    navigation,
    //
    count,
    totalPages,
    activePage,
    //
    products,
    productListProps,
    productCardProps,
    banners,
    promotion,
    //
    filters,
    orderBy,
    //
    wide = true,
    scrollToProducts,
    dataTestId
  } = props;

  // Refs

  const navigationRef = React.useRef <HTMLDivElement>(null);
  const prevRoute = React.useRef<string>(null);

  // Hooks

  const { t } = useTranslation();
  const router = useRouter();

  const {
    analyticsListProps: listProps,
    persist: persistAnalyticsListProps
  } = useRouteQueryAnalyticsListProps(productListProps);

  // Handlers

  const handleRouteChange = (url: string) => {
    if (!prevRoute.current || (prevRoute.current && !prevRoute.current.startsWith(APP_ROUTES.PRODUCTS) && !url.startsWith(APP_ROUTES.PRODUCTS))) {
      navigationRef?.current?.scrollIntoView({ block: 'center' });
    }
    prevRoute.current = url;
  };

  // Effects

  // --- Scroll on filters change

  React.useEffect(() => {
    if (scrollToProducts) {
      router.events.on('routeChangeComplete', handleRouteChange);
      return () => {
        router.events.off('routeChangeComplete', handleRouteChange);
      };
    }
  }, [router, navigationRef?.current, scrollToProducts]);

  // --- Analytics

  useAnalyticsViewItemList(
    products,
    listProps,
    { enabled: !!listProps, promotion }
  );

  // Handlers

  const onPageChange = (e: PaginationOnChangeEvent) => {
    const { selected } = e;
    const { query: { slug: segments = [] } } = router;
    const url = UrlParams.setPaginationParams(selected + 1, { segments, pageType: urlParams.pageType });

    persistAnalyticsListProps();

    router.push(url);
  };

  // Render

  return (
    <div className={classnames(styles.root, className)}>

      {/* Navigation */}
      {
        (products?.length === 0 || !categories)
          ? null
          : (
            <ProductsListingNavigation
              ref={navigationRef}
              categories={categories}
              categoryCode={categoryCode}
              {...navigation}
            />
          )
      }

      {/* Filters / sorting */}
      {
        (products?.length === 0 || (!filters && !orderBy))
          ? null
          : (
            <ListingToolbar
              className={styles.listingToolbar}
              filters={{ filters }}
              orderBy={{ orderBy }}
              pageType={urlParams.pageType}
              data={{ count }}
            />
          )
      }

      {/* Product List */}
      {
        products?.length === 0
          ? (
            <ImageMessage
              className={classnames(styles.emptyList, emptyClassName)}
              titleClassName={styles.message}
              image={IMAGES.PLACEHOLDERS.NO_PRODUCTS}
              imageProps={{
                width: 414,
                height: 246,
                alt: 'No products'
              }}
              title={t('CATEGORIES.EMPTY_TITLE')}
            />
          ) : (
            <ProductList
              wide={wide}
              products={products}
              banners={banners}
              productCardProps={productCardProps}
              productListProps={listProps}
              dataTestId={dataTestId}
            />
          )
      }

      {/* Pagination */}
      {
        products?.length > 0 && totalPages > 1 && (
          <Pagination
            activePage={activePage}
            pageCount={totalPages}
            onPageChange={onPageChange}
          />
        )
      }
    </div>
  );
};

export default ProductsListing;
